import dayjs from 'dayjs';

import { capitalizeFirstLetter, numberWithCommas, tryParseJSON } from '../../../../../utils';
import { DISCOUNT_TYPE, SEAT_STATUS, TRAINING_PRODUCT_TYPES } from '../../../../../constants';

export const getDiscount = (price, discount, type) => {
     if (!discount) return 0;

     let result = 0;
     if (type === DISCOUNT_TYPE.PRICE) {
          result = discount;
     } else {
          result = (price * discount) / 100;
     }
     return result;
};

export const calculateFinalDiscountOnItem = (order, product, couponInfo, isReturnDiscountType) => {
     if (!order || !product) return 0;

     const today = new dayjs();
     const { price, discount: productDiscount, discountType: productDiscountType, discountStartAt, discountEndAt } = product;
     // const { isCouponOnProduct, listProductApplyCoupon, discount, discountType } = couponInfo;

     const isValidDiscount = productDiscount && discountStartAt && discountEndAt && today >= dayjs(discountStartAt) && today <= dayjs(discountEndAt);
     // const isApplyCoupon = listProductApplyCoupon.length ? listProductApplyCoupon.includes(product.id) : true;

     const discountFromProduct = productDiscount && isValidDiscount ? getDiscount(price, productDiscount, productDiscountType) : 0;
     // const discountFromOrder = isCouponOnProduct && isApplyCoupon ? getDiscount(price - discountFromProduct, discount, discountType) : 0;

     const finalDiscount = discountFromProduct;

     if (isReturnDiscountType) {
          return {
               finalDiscount: discountFromProduct,
               finalDiscountType: discountFromProduct ? productDiscountType : null,
          };
     }

     return finalDiscount;
};

export const calculateTotalDiscountOnItem = (discountFromProduct, product, couponInfo, vouchersInfo = [], isReturnDiscountType) => {
     if (!product) return 0;

     const { price, discountType: productDiscountType } = product;
     // const { isCouponOnProduct, listProductApplyCoupon, discount, discountType } = couponInfo;

     // const isApplyCoupon = listProductApplyCoupon.length ? listProductApplyCoupon.includes(product.id) : true;

     const newPriceItem = price - discountFromProduct;

     // const discountFromOrder = isCouponOnProduct && isApplyCoupon ? getDiscount(newPriceItem, discount, discountType) : 0;

     const discountFromOrder = checkTotalDiscountByProduct({
          couponInfo,
          vouchersInfo,
          productId: product.id,
          price: newPriceItem,
     });

     const finalDiscount = discountFromProduct + discountFromOrder;

     if (isReturnDiscountType) {
          return {
               finalDiscount,
               finalDiscountType: discountFromProduct ? productDiscountType : null,
          };
     }

     return finalDiscount;
};

export const getToTalAfterDiscount = (price, quantity, discount, type = DISCOUNT_TYPE.PRICE, couponVoucherDisCountOnProduct = 0) => {
     if (type === DISCOUNT_TYPE.PRICE) {
          return Math.max(price - (discount + couponVoucherDisCountOnProduct), 0) * quantity;
     }

     const newDiscount = (price * discount) / 100;
     return Math.max(price - (newDiscount + couponVoucherDisCountOnProduct), 0) * quantity;
};

export const formatPrice = (number, isDiscount = false, discountType = DISCOUNT_TYPE.PRICE) => {
     const result = number
          ? numberWithCommas(
                 Number(number)
                      .toFixed(2)
                      .replace(/[.,]0$/, '')
            )
          : 0;

     switch (discountType) {
          case DISCOUNT_TYPE.PERCENT:
               return isDiscount ? `%(${result})` : '%' + result;
          default:
               return isDiscount ? `$(${result})` : '$' + result;
     }
};

export const calculateDiscountOrderInvoiceItem = (item, couponInfo, vouchersInfo) => {
     const productId = item.productId;
     const price = Number(item.price) || 0;
     const discount = Number(item.discount) || 0;
     const discountOnProduct = item.discountType === DISCOUNT_TYPE.PRICE ? discount : (price * discount) / 100;

     const totalDiscountByProduct = checkTotalDiscountByProduct({
          couponInfo,
          vouchersInfo,
          productId,
          price,
     });

     const result = Math.min(discountOnProduct + totalDiscountByProduct, price);
     return result || 0;
};

export const calculateTotalOrderInvoiceItem = (item, couponInfo, vouchersInfo) => {
     const productId = item.productId;
     const price = Number(item.price) || 0;
     const quantity = Number(item.invoicedSelectedQty || item.selectedQty || item.quantity) || 0;
     const discount = Number(item.discount) || 0;
     const discountType = item.discountType || DISCOUNT_TYPE.PRICE;

     const totalDiscountByProduct = checkTotalDiscountByProduct({
          couponInfo,
          vouchersInfo,
          productId,
          price,
     });

     const result = getToTalAfterDiscount(price, quantity, discount, discountType, totalDiscountByProduct);
     return result || 0;
};

export const getItemName = (item) => {
     const itemName = item.name?.trim() || '';
     if (!item.product) return itemName;

     const { trainingProductType } = item.product.productType;
     const isLiveTraining = trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;

     if (!isLiveTraining) return itemName;

     let name = '';

     if (item.tierId) {
          const pricingTiers = tryParseJSON(item.product?.pricingTiers) || [];
          const matchingTier = pricingTiers.find((tier) => tier.id === item.tierId);
          const suffix = matchingTier?.name && !itemName.includes(matchingTier?.name) ? matchingTier.name : '';
          name = suffix ? `${itemName} ${suffix}` : itemName;
     } else if (item.event) {
          name = item.event?.name || '';
     } else if (item.isCredit) {
          name = itemName + ' (credits)';
     } else {
          name = itemName + ' (custom date)';
     }

     return name;
};

export const checkCouponDiscountByProduct = (couponInfo, productId, price = 0) => {
     if (!couponInfo.isCouponOnProduct) return 0;

     const { listProductApplyCoupon } = couponInfo;
     const discount = Number(couponInfo.discount) || 0;

     const newDiscount = couponInfo.discountType === DISCOUNT_TYPE.PRICE ? discount : (price * discount) / 100;

     if (!listProductApplyCoupon.length) return newDiscount;
     if (listProductApplyCoupon.includes(productId)) return newDiscount;

     return 0;
};

export const checkVouchersDiscountByProduct = (vouchersInfo, productId, price = 0) => {
     if (!vouchersInfo.length) return 0;

     let _vouchersDiscountByProduct = 0;

     const _price = Number(price) || 0;
     // let priceAfterEachDiscount = _price;

     for (let i = 0; i < vouchersInfo.length; i++) {
          const voucherInfo = vouchersInfo[i];

          if (!voucherInfo.isVoucherOnProduct) continue;

          const { productListApplyVoucher } = voucherInfo;

          const discountValue = Number(voucherInfo.discountValue) || 0;
          const discountType = voucherInfo.discountType || DISCOUNT_TYPE.PRICE;

          const newDiscountValue = discountType === DISCOUNT_TYPE.PRICE ? discountValue : (_price * discountValue) / 100;
          // const newDiscountValue = discountType === DISCOUNT_TYPE.PRICE ? discountValue : (priceAfterEachDiscount * discountValue) / 100;

          const isApplyVoucherDiscount = !productListApplyVoucher?.length || productListApplyVoucher.includes(productId);

          if (isApplyVoucherDiscount) {
               _vouchersDiscountByProduct += newDiscountValue;
               // priceAfterEachDiscount -= newDiscountValue;
          }
     }

     return _vouchersDiscountByProduct;
};

export const checkTotalDiscountByProduct = ({ couponInfo, vouchersInfo, productId, price }) => {
     const couponDiscountOnProduct = checkCouponDiscountByProduct(couponInfo, productId, price);
     const vouchersDiscountOnProduct = 0;
     // const vouchersDiscountOnProduct = checkVouchersDiscountByProduct(vouchersInfo, productId, price);

     return couponDiscountOnProduct + vouchersDiscountOnProduct;
};

export const getSeatStatus2 = (status) => {
     if (!status) {
          return '';
     }

     let nameStatus = '';

     switch (status) {
          case SEAT_STATUS.NOT_STARTED:
               nameStatus = 'Not Started';
               break;
          case SEAT_STATUS.IN_PROGRESS:
               nameStatus = 'In Process';
               break;
          case SEAT_STATUS.ACCESS_REMOVED:
               nameStatus = 'Access Removed';
               break;
          default:
               nameStatus = status;
               break;
     }

     return capitalizeFirstLetter(nameStatus);
};
